// src/components/LaporanKehadiran.jsx
import { useState, useEffect, useMemo } from 'react';
import XLSX from 'xlsx-js-style'; // fork dari SheetJS yang mendukung border/warna saat menulis .xlsx
import { api } from '../services/api';

const STATUS_LIST = ['Hadir', 'Sakit', 'Izin', 'Alpha', 'Terlambat'];

const STATUS_BADGE = {
  Hadir: 'bg-green-100 text-green-700',
  Sakit: 'bg-amber-100 text-amber-700',
  Izin: 'bg-blue-100 text-blue-700',
  Alpha: 'bg-red-100 text-red-700',
  Terlambat: 'bg-purple-100 text-purple-700',
  'Tidak Berlaku': 'bg-slate-100 text-slate-500',
};

function formatTanggal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

// "Selasa, 21-07-2026" — meniru format baris tanggal di Excel rekap sekolah
function formatTanggalPanjang(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const hari = d.toLocaleDateString('id-ID', { weekday: 'long' });
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${hari}, ${dd}-${mm}-${yyyy}`;
}

export default function LaporanKehadiran({ onBack }) {
  const [siswaAll, setSiswaAll] = useState([]);
  const [error, setError] = useState('');

  const [jenis, setJenis] = useState('harian');
  const [tanggal, setTanggal] = useState(() => new Date().toISOString().slice(0, 10));
  const [bulanAwal, setBulanAwal] = useState(() => new Date().toISOString().slice(0, 7));
  const [bulanAkhir, setBulanAkhir] = useState(() => new Date().toISOString().slice(0, 7));
  const [kelasFilter, setKelasFilter] = useState('Semua Kelas');
  const [idSiswa, setIdSiswa] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showBobot, setShowBobot] = useState(false);
  // Bobot kredit tiap status untuk menghitung "Nilai Kehadiran" (skala 0-1, dikali 100).
  // Hadir selalu 1.00 dan Alpha selalu 0.00 (jangkar atas-bawah), yang bisa diatur cuma
  // Sakit/Izin/Terlambat sesuai kebijakan sekolah.
  const [bobot, setBobot] = useState({ sakit: 0.75, izin: 0.65, terlambat: 0.5 });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getSiswa('Semua Kelas');
        setSiswaAll(res.data || []);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, []);

  const kelasList = useMemo(
    () => Array.from(new Set(siswaAll.map((s) => s.Kelas).filter(Boolean))).sort(),
    [siswaAll]
  );

  async function handleBuatLaporan() {
    setError('');
    if (jenis === 'harian' && !tanggal) { setError('Pilih tanggal.'); return; }
    if (jenis === 'bulanan' && (!bulanAwal || !bulanAkhir)) { setError('Pilih bulan awal dan bulan akhir.'); return; }
    if (jenis === 'bulanan' && bulanAwal > bulanAkhir) { setError('Bulan awal tidak boleh sesudah bulan akhir.'); return; }
    if (jenis === 'per_siswa' && !idSiswa) { setError('Pilih siswa.'); return; }

    setLoading(true);
    setExpandedId(null);
    try {
      const params = { jenis };
      if (jenis === 'harian') { params.tanggal = tanggal; params.kelas = kelasFilter; }
      if (jenis === 'bulanan') { params.bulanAwal = bulanAwal; params.bulanAkhir = bulanAkhir; params.kelas = kelasFilter; }
      if (jenis === 'per_siswa') { params.idSiswa = idSiswa; }

      const res = await api.getLaporan(params);
      setData(res.data || []);
      setHasGenerated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const ta = String(a.Tanggal);
      const tb = String(b.Tanggal);
      if (ta !== tb) return ta < tb ? -1 : 1;
      return String(a.NamaSiswa).localeCompare(String(b.NamaSiswa));
    });
  }, [data]);

  const tally = useMemo(() => {
    const t = {};
    STATUS_LIST.forEach((st) => { t[st] = data.filter((r) => r.Status === st).length; });
    return t;
  }, [data]);

  // ===== REKAP BULANAN: per siswa, dihitung PER JAM (sama seperti di file Excel) =====
  // Setiap baris kehadiran (1 baris = 1 siswa di 1 jam pelajaran) dihitung apa adanya,
  // supaya "Total Jam" & persentase di layar sama persis dengan angka di Excel.
  const rekapBulanan = useMemo(() => {
    if (jenis !== 'bulanan') return [];

    const perSiswa = {}; // idSiswa -> { hadir, alpha, sakit, izin, terlambat, rincian: [{tanggal,jamKe,status}] }
    const namaMap = {};
    data.forEach((r) => {
      if (r.Status === 'Tidak Berlaku') return; // siswa belum resmi terdaftar saat itu — tidak dihitung sama sekali
      if (!perSiswa[r.ID_Siswa]) {
        perSiswa[r.ID_Siswa] = { hadir: 0, alpha: 0, sakit: 0, izin: 0, terlambat: 0, rincian: [] };
      }
      namaMap[r.ID_Siswa] = r.NamaSiswa;
      const rec = perSiswa[r.ID_Siswa];
      if (r.Status === 'Hadir') rec.hadir++;
      else {
        if (r.Status === 'Alpha') rec.alpha++;
        else if (r.Status === 'Sakit') rec.sakit++;
        else if (r.Status === 'Izin') rec.izin++;
        else if (r.Status === 'Terlambat') rec.terlambat++;
        rec.rincian.push({ tanggal: r.Tanggal, jamKe: r.JamKe, status: r.Status });
      }
    });

    const hasil = Object.keys(perSiswa).map((idSiswaKey) => {
      const rec = perSiswa[idSiswaKey];
      const info = siswaAll.find((s) => s.ID === idSiswaKey);
      const totalJam = rec.hadir + rec.alpha + rec.sakit + rec.izin + rec.terlambat;
      const totalBobot = rec.hadir * 1 + rec.sakit * bobot.sakit + rec.izin * bobot.izin + rec.terlambat * bobot.terlambat + rec.alpha * 0;
      const nilaiKehadiran = totalJam > 0 ? Math.round((totalBobot / totalJam) * 1000) / 10 : null;
      rec.rincian.sort((a, b) => (a.tanggal === b.tanggal ? a.jamKe - b.jamKe : a.tanggal < b.tanggal ? -1 : 1));
      return {
        idSiswa: idSiswaKey,
        nama: namaMap[idSiswaKey] || idSiswaKey,
        kelas: info?.Kelas || '',
        jk: info?.JenisKelamin || '',
        hadir: rec.hadir, alpha: rec.alpha, sakit: rec.sakit, izin: rec.izin, terlambat: rec.terlambat,
        totalJam,
        nilaiKehadiran,
        rincian: rec.rincian,
      };
    });

    hasil.sort((a, b) => a.nama.localeCompare(b.nama));
    return hasil;
  }, [data, jenis, siswaAll, bobot]);

  function handleExport() {
    if (jenis === 'bulanan') {
      exportRekapBulananExcel();
      return;
    }

    const rows = sorted.map((r) => ({
      Tanggal: formatTanggal(r.Tanggal),
      Kelas: r.Kelas,
      'Mata Pelajaran': r.MataPelajaran,
      'Jam Ke': r.JamKe,
      'Nama Siswa': r.NamaSiswa,
      Status: r.Status,
      Catatan: r.Catatan,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Laporan');
    XLSX.writeFile(wb, `Laporan_Kehadiran_${jenis}_${Date.now()}.xlsx`);
  }

  // Export rekap bulanan meniru format Excel manual sekolah: 1 sheet per kelas,
  // kolom dikelompokkan per "Pertemuan" (tanggal). Jumlah sub-kolom Jam ke- pada
  // tiap pertemuan MENGIKUTI berapa jam yang benar-benar diinput hari itu (2 atau 3),
  // bukan dipaksa selalu 3. Plus rekap JML HADIR / Ketidakhadiran (A/I/S/T) /
  // Total Jam / Persentase, lengkap dengan border tabel.
  function exportRekapBulananExcel() {
    const kelasAktif = Array.from(new Set(siswaAll.filter((s) => s.Status === 'Aktif').map((s) => s.Kelas))).filter(Boolean);
    const kelasTarget = kelasFilter !== 'Semua Kelas' ? [kelasFilter] : Array.from(new Set(data.map((r) => r.Kelas))).filter((k) => kelasAktif.includes(k)).sort();

    if (kelasTarget.length === 0) {
      setError('Tidak ada data kelas untuk diekspor.');
      return;
    }

    const STATUS_CODE = { Hadir: 1, Sakit: 'S', Izin: 'I', Alpha: 'A', Terlambat: 'T', 'Tidak Berlaku': 'TB' };

    const THIN = { style: 'thin', color: { rgb: 'A0A0A0' } };
    const BORDER_ALL = { top: THIN, bottom: THIN, left: THIN, right: THIN };
    const headerStyle = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      fill: { fgColor: { rgb: 'DCE6F7' } },
      border: BORDER_ALL,
    };
    const titleStyle = { font: { bold: true, sz: 12 } };
    const dataCenterStyle = { alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER_ALL, font: { sz: 10 } };
    const dataLeftStyle = { alignment: { horizontal: 'left', vertical: 'center' }, border: BORDER_ALL, font: { sz: 10 } };

    const wb = XLSX.utils.book_new();

    kelasTarget.forEach((kelasNama) => {
      const rosterKelas = siswaAll
        .filter((s) => s.Kelas === kelasNama && s.Status === 'Aktif')
        .sort((a, b) => String(a.Nama).localeCompare(String(b.Nama)));
      const recordsKelas = data.filter((r) => r.Kelas === kelasNama);
      const tanggalUnik = Array.from(new Set(recordsKelas.map((r) => r.Tanggal))).sort();

      // Jumlah kolom Jam ke- pada tiap pertemuan MENGIKUTI data asli (2 atau 3), bukan dipaksa 3.
      const jamPerPertemuan = {};
      tanggalUnik.forEach((t) => {
        const jams = recordsKelas.filter((r) => r.Tanggal === t).map((r) => Number(r.JamKe));
        jamPerPertemuan[t] = jams.length ? Math.max(...jams) : 1;
      });
      const totalJamSemuaPertemuan = tanggalUnik.reduce((sum, t) => sum + jamPerPertemuan[t], 0);

      const lookup = {}; // idSiswa -> tanggal -> jamKe -> status
      recordsKelas.forEach((r) => {
        if (!lookup[r.ID_Siswa]) lookup[r.ID_Siswa] = {};
        if (!lookup[r.ID_Siswa][r.Tanggal]) lookup[r.ID_Siswa][r.Tanggal] = {};
        lookup[r.ID_Siswa][r.Tanggal][r.JamKe] = r.Status;
      });

      const totalDataCols = 3 + totalJamSemuaPertemuan + 1 /*JML HADIR*/ + 4 /*A/I/S/T*/ + 1 /*TOTAL JAM*/ + 1 /*PERSENTASE*/ + 1 /*NILAI*/;
      const lastCol = totalDataCols - 1;

      const aoa = [];
      aoa.push(['REKAP KEHADIRAN SISWA']);
      aoa.push(['UPT SMAN 3 Luwu Utara']);
      aoa.push([`Periode: ${bulanAwal} s.d. ${bulanAkhir}`]);
      aoa.push([`Kelas ${kelasNama}`]);

      const headerRow1 = ['NO', 'NAMA SISWA', 'L/P'];
      tanggalUnik.forEach((t, i) => {
        headerRow1.push(`Pertemuan ${i + 1}`);
        for (let k = 1; k < jamPerPertemuan[t]; k++) headerRow1.push('');
      });
      headerRow1.push('JML\nHADIR', 'KETIDAKHADIRAN', '', '', '', 'TOTAL\nJAM', 'PERSEN\nTASE', 'NILAI');
      aoa.push(headerRow1);

      const headerRow2 = ['', '', ''];
      tanggalUnik.forEach((t) => {
        headerRow2.push(formatTanggalPanjang(t));
        for (let k = 1; k < jamPerPertemuan[t]; k++) headerRow2.push('');
      });
      headerRow2.push('', 'A', 'I', 'S', 'T', '', '', '');
      aoa.push(headerRow2);

      const headerRow3 = ['', '', ''];
      tanggalUnik.forEach((t) => {
        for (let k = 1; k <= jamPerPertemuan[t]; k++) headerRow3.push(k);
      });
      headerRow3.push('', '', '', '', '', '', '', '');
      aoa.push(headerRow3);

      const headerRowStartIdx = 4; // baris index (0-based) tempat headerRow1 berada di aoa
      const dataStartIdx = headerRowStartIdx + 3; // headerRow1, headerRow2, headerRow3

      rosterKelas.forEach((s, idx) => {
        const row = [idx + 1, s.Nama, s.JenisKelamin === 'Perempuan' ? 'P' : 'L'];
        let hadir = 0, alpha = 0, izin = 0, sakit = 0, terlambat = 0;
        tanggalUnik.forEach((t) => {
          for (let jamKe = 1; jamKe <= jamPerPertemuan[t]; jamKe++) {
            const status = lookup[s.ID]?.[t]?.[jamKe];
            if (!status) { row.push(''); continue; }
            row.push(STATUS_CODE[status] ?? '');
            if (status === 'Hadir') hadir++;
            else if (status === 'Alpha') alpha++;
            else if (status === 'Izin') izin++;
            else if (status === 'Sakit') sakit++;
            else if (status === 'Terlambat') terlambat++;
          }
        });
        const totalTercatat = hadir + alpha + izin + sakit + terlambat;
        const persentase = totalTercatat > 0 ? `${Math.round((hadir / totalTercatat) * 1000) / 10}%` : '-';
        const totalBobot = hadir * 1 + sakit * bobot.sakit + izin * bobot.izin + terlambat * bobot.terlambat + alpha * 0;
        const nilai = totalTercatat > 0 ? Math.round((totalBobot / totalTercatat) * 1000) / 10 : '-';
        row.push(hadir, alpha, izin, sakit, terlambat, totalTercatat, persentase, nilai);
        aoa.push(row);
      });

      // FOOTER: keterangan kode status + ringkasan jumlah siswa
      const laki = rosterKelas.filter((s) => s.JenisKelamin !== 'Perempuan').length;
      const perempuan = rosterKelas.filter((s) => s.JenisKelamin === 'Perempuan').length;
      const footerStartIdx = dataStartIdx + rosterKelas.length + 1; // +1 baris kosong pemisah
      aoa.push([]);
      aoa.push(['KETERANGAN:', '', '', `JUMLAH SISWA : ${rosterKelas.length}`]);
      aoa.push(['A', ': Alpha', '', `LAKI-LAKI : ${laki}`]);
      aoa.push(['I', ': Izin', '', `PEREMPUAN : ${perempuan}`]);
      aoa.push(['S', ': Sakit', '', `TOTAL JAM SELURUH PERTEMUAN : ${totalJamSemuaPertemuan}`]);
      aoa.push(['T', ': Terlambat', '', `BOBOT NILAI — Hadir 1.00, Sakit ${bobot.sakit.toFixed(2)}, Izin ${bobot.izin.toFixed(2)}, Terlambat ${bobot.terlambat.toFixed(2)}, Alpha 0.00`]);
      aoa.push(['TB', ': Tidak Berlaku (siswa pindahan, belum terdaftar saat itu — tidak dihitung ke persentase/nilai)']);

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // ===== MERGE SEL =====
      const merges = [];
      [0, 1, 2, 3].forEach((r) => merges.push({ s: { r, c: 0 }, e: { r, c: lastCol } }));
      let col = 3;
      tanggalUnik.forEach((t) => {
        const span = jamPerPertemuan[t];
        if (span > 1) {
          merges.push({ s: { r: headerRowStartIdx, c: col }, e: { r: headerRowStartIdx, c: col + span - 1 } });
          merges.push({ s: { r: headerRowStartIdx + 1, c: col }, e: { r: headerRowStartIdx + 1, c: col + span - 1 } });
        }
        col += span;
      });
      merges.push({ s: { r: headerRowStartIdx, c: col + 1 }, e: { r: headerRowStartIdx, c: col + 4 } }); // KETIDAKHADIRAN
      [0, 1, 2].forEach((c) => merges.push({ s: { r: headerRowStartIdx, c }, e: { r: headerRowStartIdx + 2, c } })); // NO/NAMA/L-P
      merges.push({ s: { r: headerRowStartIdx, c: col }, e: { r: headerRowStartIdx + 2, c: col } }); // JML HADIR
      merges.push({ s: { r: headerRowStartIdx, c: col + 5 }, e: { r: headerRowStartIdx + 2, c: col + 5 } }); // TOTAL JAM
      merges.push({ s: { r: headerRowStartIdx, c: col + 6 }, e: { r: headerRowStartIdx + 2, c: col + 6 } }); // PERSENTASE
      merges.push({ s: { r: headerRowStartIdx, c: col + 7 }, e: { r: headerRowStartIdx + 2, c: col + 7 } }); // NILAI
      merges.push({ s: { r: footerStartIdx, c: 0 }, e: { r: footerStartIdx, c: 2 } }); // label "KETERANGAN:" biar tidak terpotong
      ws['!merges'] = merges;

      ws['!cols'] = [
        { wch: 4 }, { wch: 24 }, { wch: 5 },
        ...Array(totalJamSemuaPertemuan).fill({ wch: 7 }),
        { wch: 8 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 6 }, { wch: 8 }, { wch: 9 }, { wch: 8 },
      ];

      // Tinggi baris: judul normal, tapi 3 baris header tabel (Pertemuan / Tanggal / Jam ke-)
      // dibuat lebih tinggi supaya tulisan tanggal yang di-wrap tidak terpotong.
      ws['!rows'] = [
        { hpt: 20 }, { hpt: 16 }, { hpt: 16 }, { hpt: 16 }, // 4 baris judul
        { hpt: 20 },  // baris "Pertemuan N"
        { hpt: 42 },  // baris tanggal (paling butuh ruang, di-wrap 2 baris)
        { hpt: 18 },  // baris nomor Jam ke-
        ...Array(rosterKelas.length).fill({ hpt: 18 }),
      ];

      // ===== STYLING: border + bold + rata tengah =====
      for (let r = 0; r < 4; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) ws[addr].s = titleStyle;
      }
      for (let r = headerRowStartIdx; r <= headerRowStartIdx + 2; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          ws[addr].s = headerStyle;
        }
      }
      for (let r = dataStartIdx; r < dataStartIdx + rosterKelas.length; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) ws[addr] = { t: 's', v: '' };
          ws[addr].s = c === 1 ? dataLeftStyle : dataCenterStyle;
        }
      }
      // sedikit penebalan pada baris keterangan
      for (let r = footerStartIdx; r < footerStartIdx + 6; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) ws[addr].s = { font: { bold: r === footerStartIdx } };
      }

      // nama sheet Excel maksimal 31 karakter & tidak boleh karakter tertentu
      const sheetName = String(kelasNama).replace(/[\\/*?:[\]]/g, '-').slice(0, 31) || 'Kelas';
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `Rekap_Kehadiran_${bulanAwal}_${bulanAkhir}.xlsx`);
  }

  const inputCls =
    'w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';

  const jumlahHasil = jenis === 'bulanan' ? rekapBulanan.length : sorted.length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="border-l-4 border-blue-600 pl-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Laporan Kehadiran</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Harian, bulanan, atau per siswa</p>
          </div>
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            &larr; Kembali ke Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        {/* KRITERIA LAPORAN */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-blue-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128202;</span> Pilih Kriteria Laporan
            </h2>
          </div>
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Jenis Laporan</label>
                <select value={jenis} onChange={(e) => setJenis(e.target.value)} className={inputCls}>
                  <option value="harian">Harian</option>
                  <option value="bulanan">Bulanan/Periode (Rekap per Siswa)</option>
                  <option value="per_siswa">Per Siswa</option>
                </select>
              </div>

              {jenis === 'harian' && (
                <>
                  <div>
                    <label className={labelCls}>Tanggal</label>
                    <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Kelas</label>
                    <select value={kelasFilter} onChange={(e) => setKelasFilter(e.target.value)} className={inputCls}>
                      <option>Semua Kelas</option>
                      {kelasList.map((k) => <option key={k}>{k}</option>)}
                    </select>
                  </div>
                </>
              )}

              {jenis === 'bulanan' && (
                <>
                  <div>
                    <label className={labelCls}>Dari Bulan</label>
                    <input type="month" value={bulanAwal} onChange={(e) => setBulanAwal(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Sampai Bulan</label>
                    <input type="month" value={bulanAkhir} onChange={(e) => setBulanAkhir(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Kelas</label>
                    <select value={kelasFilter} onChange={(e) => setKelasFilter(e.target.value)} className={inputCls}>
                      <option>Semua Kelas</option>
                      {kelasList.map((k) => <option key={k}>{k}</option>)}
                    </select>
                  </div>
                </>
              )}

              {jenis === 'per_siswa' && (
                <div className="sm:col-span-2">
                  <label className={labelCls}>Siswa</label>
                  <select value={idSiswa} onChange={(e) => setIdSiswa(e.target.value)} className={inputCls}>
                    <option value="">-- Pilih Siswa --</option>
                    {siswaAll.map((s) => (
                      <option key={s.ID} value={s.ID}>{s.Nama} — {s.Kelas}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {jenis === 'bulanan' && (
              <>
                <p className="text-xs text-slate-400 mt-3">
                  Isi "Dari Bulan" dan "Sampai Bulan" dengan bulan yang sama kalau cuma mau 1 bulan.
                  Hasilnya berupa rekap per siswa (mirip format Excel): total hari Hadir/Alpha/Izin/Sakit/Terlambat,
                  lengkap dengan rincian tanggal-tanggal tidak hadirnya dan Nilai Kehadiran.
                </p>

                <button
                  onClick={() => setShowBobot((v) => !v)}
                  className="text-xs font-medium text-blue-600 hover:underline mt-2"
                >
                  {showBobot ? 'Sembunyikan' : 'Atur'} bobot Nilai Kehadiran
                </button>

                {showBobot && (
                  <div className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <p className="text-xs text-slate-500 mb-3">
                      Nilai Kehadiran = (Σ bobot semua jam ÷ Total Jam) × 100. Hadir selalu bobot 1.00, Alpha selalu 0.00 —
                      yang bisa diatur cuma Sakit, Izin, dan Terlambat (skala 0–1).
                    </p>
                    <div className="grid grid-cols-3 gap-3 max-w-md">
                      <div>
                        <label className={labelCls}>Sakit</label>
                        <input
                          type="number" min="0" max="1" step="0.05"
                          value={bobot.sakit}
                          onChange={(e) => setBobot((b) => ({ ...b, sakit: Math.min(1, Math.max(0, Number(e.target.value))) }))}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Izin</label>
                        <input
                          type="number" min="0" max="1" step="0.05"
                          value={bobot.izin}
                          onChange={(e) => setBobot((b) => ({ ...b, izin: Math.min(1, Math.max(0, Number(e.target.value))) }))}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Terlambat</label>
                        <input
                          type="number" min="0" max="1" step="0.05"
                          value={bobot.terlambat}
                          onChange={(e) => setBobot((b) => ({ ...b, terlambat: Math.min(1, Math.max(0, Number(e.target.value))) }))}
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={handleBuatLaporan}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium text-sm px-6 py-2.5 rounded-lg transition"
              >
                {loading ? 'Memuat...' : 'Buat Laporan'}
              </button>
            </div>
          </div>
        </section>

        {/* HASIL LAPORAN */}
        {hasGenerated && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-indigo-600 px-5 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <span aria-hidden="true">&#128203;</span>
                {jenis === 'bulanan' ? `Rekap ${jumlahHasil} siswa` : `Hasil Laporan (${jumlahHasil} catatan)`}
              </h2>
              {jumlahHasil > 0 && (
                <button
                  onClick={handleExport}
                  className="bg-white text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full hover:bg-indigo-50 transition"
                >
                  Unduh Excel
                </button>
              )}
            </div>

            {jumlahHasil === 0 ? (
              <p className="text-center text-slate-500 py-10">Tidak ada data untuk kriteria ini.</p>
            ) : jenis === 'bulanan' ? (
              <>
                {/* RINGKASAN TOTAL */}
                <div className="flex flex-wrap gap-2 px-5 sm:px-6 py-4 border-b border-slate-100">
                  {STATUS_LIST.map((st) => (
                    <span key={st} className={`text-xs font-medium px-3 py-1.5 rounded-full ${STATUS_BADGE[st]}`}>
                      {st}: {tally[st]}
                    </span>
                  ))}
                </div>

                <div className="hidden sm:flex items-center gap-3 px-6 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  <span className="flex-1">Nama Siswa</span>
                  <span className="w-16 shrink-0 text-center">Hadir</span>
                  <span className="w-16 shrink-0 text-center">Alpha</span>
                  <span className="w-16 shrink-0 text-center">Izin</span>
                  <span className="w-16 shrink-0 text-center">Sakit</span>
                  <span className="w-20 shrink-0 text-center">Terlambat</span>
                  <span className="w-16 shrink-0 text-center">Total Jam</span>
                  <span className="w-16 shrink-0 text-center">%Hadir</span>
                  <span className="w-16 shrink-0 text-center">Nilai</span>
                  <span className="w-8 shrink-0" />
                </div>
                <div className="border-t border-slate-100" />

                <div className="divide-y divide-slate-100">
                  {rekapBulanan.map((r) => {
                    const persen = r.totalJam > 0 ? Math.round((r.hadir / r.totalJam) * 100) : 0;
                    const isOpen = expandedId === r.idSiswa;
                    return (
                      <div key={r.idSiswa}>
                        {/* ===== VERSI HP: kartu dengan label di tiap angka ===== */}
                        <button
                          onClick={() => setExpandedId(isOpen ? null : r.idSiswa)}
                          className="sm:hidden w-full text-left p-4 hover:bg-slate-50 transition"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-slate-800">
                              {r.nama} <span className="text-xs text-slate-400 font-normal">({r.kelas})</span>
                            </span>
                            <span className="text-slate-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                          </div>
                          <div className="grid grid-cols-4 gap-y-2 text-center">
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Hadir</p>
                              <p className="text-sm font-semibold text-green-700">{r.hadir}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Alpha</p>
                              <p className="text-sm font-semibold text-red-700">{r.alpha}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Izin</p>
                              <p className="text-sm font-semibold text-blue-700">{r.izin}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Sakit</p>
                              <p className="text-sm font-semibold text-amber-700">{r.sakit}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Terlambat</p>
                              <p className="text-sm font-semibold text-purple-700">{r.terlambat}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Total Jam</p>
                              <p className="text-sm font-semibold text-slate-600">{r.totalJam}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">%Hadir</p>
                              <p className="text-sm font-semibold text-slate-700">{persen}%</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Nilai</p>
                              <p className="text-sm font-bold text-indigo-700">{r.nilaiKehadiran !== null ? r.nilaiKehadiran : '-'}</p>
                            </div>
                          </div>
                        </button>

                        {/* ===== VERSI TABLET/DESKTOP: 1 baris rata kanan-kiri ===== */}
                        <button
                          onClick={() => setExpandedId(isOpen ? null : r.idSiswa)}
                          className="hidden sm:flex w-full flex-wrap items-center gap-3 px-4 sm:px-6 py-3 text-left hover:bg-slate-50 transition"
                        >
                          <span className="flex-1 text-sm font-medium text-slate-800">
                            {r.nama} <span className="text-xs text-slate-400">({r.kelas})</span>
                          </span>
                          <span className="w-16 shrink-0 text-center text-xs font-medium text-green-700">{r.hadir}</span>
                          <span className="w-16 shrink-0 text-center text-xs font-medium text-red-700">{r.alpha}</span>
                          <span className="w-16 shrink-0 text-center text-xs font-medium text-blue-700">{r.izin}</span>
                          <span className="w-16 shrink-0 text-center text-xs font-medium text-amber-700">{r.sakit}</span>
                          <span className="w-20 shrink-0 text-center text-xs font-medium text-purple-700">{r.terlambat}</span>
                          <span className="w-16 shrink-0 text-center text-xs font-semibold text-slate-600">{r.totalJam}</span>
                          <span className="w-16 shrink-0 text-center text-xs font-semibold text-slate-700">{persen}%</span>
                          <span className="w-16 shrink-0 text-center text-xs font-bold text-indigo-700">
                            {r.nilaiKehadiran !== null ? r.nilaiKehadiran : '-'}
                          </span>
                          <span className="w-8 shrink-0 text-center text-slate-400">{isOpen ? '▲' : '▼'}</span>
                        </button>

                        {isOpen && (
                          <div className="px-4 sm:px-6 pb-4 bg-slate-50/60">
                            {r.rincian.length === 0 ? (
                              <p className="text-xs text-slate-400 py-2">Hadir penuh, tidak ada catatan ketidakhadiran.</p>
                            ) : (
                              <div className="flex flex-wrap gap-2 pt-2">
                                {r.rincian.map((x, i) => (
                                  <span key={i} className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_BADGE[x.status]}`}>
                                    {formatTanggal(x.tanggal)} (Jam ke-{x.jamKe}) — {x.status}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                {/* RINGKASAN STATUS */}
                <div className="flex flex-wrap gap-2 px-5 sm:px-6 py-4 border-b border-slate-100">
                  {STATUS_LIST.map((st) => (
                    <span key={st} className={`text-xs font-medium px-3 py-1.5 rounded-full ${STATUS_BADGE[st]}`}>
                      {st}: {tally[st]}
                    </span>
                  ))}
                </div>

                <div className="hidden sm:flex items-center gap-3 px-6 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                  <span className="w-24 shrink-0">Tanggal</span>
                  <span className="w-16 shrink-0">Kelas</span>
                  <span className="flex-1">Nama Siswa</span>
                  <span className="w-24 shrink-0">Jam Ke</span>
                  <span className="w-20 shrink-0 text-center">Status</span>
                  <span className="w-32 shrink-0">Catatan</span>
                </div>
                <div className="border-t border-slate-100" />

                {/* ===== VERSI HP ===== */}
                <div className="sm:hidden divide-y divide-slate-100">
                  {sorted.map((r, i) => (
                    <div key={i} className="p-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs text-slate-500">{formatTanggal(r.Tanggal)} · Jam ke-{r.JamKe}</span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${STATUS_BADGE[r.Status] || 'bg-slate-100 text-slate-600'}`}>
                          {r.Status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        {r.NamaSiswa} <span className="text-xs font-normal text-slate-400">({r.Kelas})</span>
                      </p>
                      {r.Catatan && <p className="text-xs text-slate-400 mt-1">{r.Catatan}</p>}
                    </div>
                  ))}
                </div>

                {/* ===== VERSI TABLET/DESKTOP ===== */}
                <div className="hidden sm:block divide-y divide-slate-100">
                  {sorted.map((r, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3">
                      <span className="w-24 shrink-0 text-xs text-slate-500">{formatTanggal(r.Tanggal)}</span>
                      <span className="w-16 shrink-0 text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-1 text-center">
                        {r.Kelas}
                      </span>
                      <span className="flex-1 text-sm font-medium text-slate-800">{r.NamaSiswa}</span>
                      <span className="w-24 shrink-0 text-xs text-slate-400 truncate">Jam ke-{r.JamKe}</span>
                      <span className={`w-20 shrink-0 text-center text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGE[r.Status] || 'bg-slate-100 text-slate-600'}`}>
                        {r.Status}
                      </span>
                      <span className="w-32 shrink-0 text-xs text-slate-400 truncate" title={r.Catatan}>{r.Catatan || '-'}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
